<template>
  <modal-vue
    @close="close"
    :modalActive="modalActive"
    :has-header-border="true"
    :has-footer-border="true"
  >
    <template #header>
      <p class="font-bold text-start text-lg">Criar usuário</p>
    </template>

    <v-form ref="formRef" @submit.prevent="createUser">
      <!--
        <v-row dense>
          <v-col cols="12">
            <p class="font-bold my-2 text-start">Acesso total ao sistema 11111</p>
            <v-checkbox v-model="form.fullAccess" hide-details label="Conceder acesso total" />
          </v-col>
        </v-row>
      -->

      <v-row dense class="mt-4">
        <v-col cols="12" md="6">
          <GenericInput
            v-model="form.name"
            label="Nome*"
            :rules="[rules.required, rules.name]"
            variant="outlined"
          />
        </v-col>

        <v-col cols="12" md="6">
          <GenericInput
            v-model="form.email"
            label="Email*"
            :rules="[rules.required, rules.email]"
            variant="outlined"
          />
        </v-col>

        <v-col cols="12" md="6">
          <GenericInput
            v-model="form.phone1"
            label="Telefone*"
            :rules="[rules.required, rules.phone]"
            v-maska="'(##) #####-####'"
            variant="outlined"
          />
        </v-col>

        <v-col cols="12" md="6">
          <GenericInput
            v-model="form.registration"
            label="Matrícula"
            :rules="[]"
            variant="outlined"
          />
        </v-col>

        <v-col cols="12" md="6">
          <GenericInput
            v-model="form.password"
            :disabled="form.sendWelcomeEmail"
            :rules="form.sendWelcomeEmail ? [] : [rules.required, rules.password]"
            label="Senha"
            type="password"
            variant="outlined"
          />
        </v-col>

        <v-col cols="12" md="6">
          <GenericInput
            v-model="form.confirmPassword"
            :disabled="form.sendWelcomeEmail"
            :rules="form.sendWelcomeEmail ? [] : [rules.required, rules.confirmPassword]"
            label="Confirmar senha"
            type="password"
            variant="outlined"
          />
        </v-col>

        <v-col cols="12">
          <v-autocomplete
            v-model="form.companies"
            color="primary20"
            label="Empresas*"
            :items="listCompanies"
            item-title="title"
            item-value="value"
            multiple
            chips
            closable-chips
            :rules="[rules.requiredList]"
            variant="outlined"
          />
        </v-col>

        <v-col cols="12">
          <v-autocomplete
            v-model="form.groups"
            color="primary20"
            label="Grupos"
            :items="groupOptions"
            :loading="isGroupsLoading"
            :disabled="form.companies.length === 0"
            item-title="title"
            item-value="value"
            multiple
            clearable
            placeholder="Selecione os grupos (opcional)"
            no-data-text="Nenhum grupo encontrado para as empresas selecionadas"
            :rules="[]"
            variant="outlined"
          >
            <template v-slot:selection="{ item, index }">
              <v-chip v-if="index < 4" closable @click:close="removeGroup(item.raw.groupId)">
                <span>{{ item.raw.groupName }}</span>
              </v-chip>

              <span v-if="index === 4" class="text-grey text-caption align-self-center ml-2">
                (+{{ form.groups.length - 4 }})
              </span>
            </template>

            <template v-slot:item="{ props, item }">
              <v-list-item v-bind="props">
                <template v-slot:prepend>
                  <v-checkbox-btn
                    :model-value="form.groups.includes(item.value)"
                    pointer-events="none"
                  ></v-checkbox-btn>
                </template>
                <template v-slot:title>
                  <span class="font-weight-medium">
                    {{ item.raw.groupName }}
                  </span>
                </template>
                <template v-slot:append>
                  <span class="text-grey-lighten-1 font-weight-regular" style="font-size: 0.65rem">
                    Empresa: {{ item.raw.companyName }}
                  </span>
                </template>
              </v-list-item>
            </template>

            <template v-slot:append-item>
              <div
                v-if="hasMoreGroups"
                v-intersect="{ handler: onIntersect, options: { threshold: 0.5 } }"
                class="pa-4 d-flex justify-center"
              >
                <v-progress-circular
                  v-if="isGroupsLoading"
                  indeterminate
                  size="20"
                  color="primary"
                />
              </div>
            </template>
          </v-autocomplete>
        </v-col>

        <v-col cols="12">
          <v-checkbox
            v-model="form.sendWelcomeEmail"
            hide-details
            label="Enviar e-mail de boas-vindas com as credenciais para o novo usuário"
          />
        </v-col>
      </v-row>
    </v-form>

    <template #footer>
      <v-row dense justify="end">
        <v-col cols="12" md="auto">
          <OutlineButton @click="close" text="Cancelar" width="140px" />
        </v-col>
        <v-col cols="12" md="auto">
          <FilledButton
            :loading="loading"
            @click="createUser"
            text="Criar"
            width="140px"
            type="submit"
          />
        </v-col>
      </v-row>
    </template>
  </modal-vue>
</template>

<script setup lang="ts">
import { computed, reactive, ref, watch } from 'vue'
import { VForm } from 'vuetify/components'
import ModalVue from '@/components/designSystem/modals/Modal.vue'
import OutlineButton from '@/components/designSystem/Buttons/OutlineButton/index.vue'
import FilledButton from '@/components/designSystem/Buttons/FilledButton/index.vue'
import { storeToRefs } from 'pinia'
import { useCompanyStore } from '@/pinia-store/useCompanyStore'
import Network from '@/Network'
import { useToast } from 'vue-toastification'
import { useErrorToast } from '@/composables/useErrorToast'
import GenericInput from '@/components/designSystem/Inputs/GenericInput.vue'
import { useGroupStore } from '@/pinia-store/useGroupStore'
import { Intersect as vIntersect } from 'vuetify/directives'

defineProps<{ modalActive: boolean }>()
const emits = defineEmits(['close'])

const formRef = ref<InstanceType<typeof VForm> | null>(null)
const loading = ref(false)
const { listCompanies } = storeToRefs(useCompanyStore())
const groupStore = useGroupStore()
const { filteredGroups, isLoading: isGroupsLoading } = storeToRefs(groupStore)
const toast = useToast()
const { notifyError } = useErrorToast()

const getInitialFormState = () => ({
  name: '',
  email: '',
  password: '',
  confirmPassword: '',
  phone1: '',
  registration: '',
  fullAccess: false,
  sendWelcomeEmail: true,
  companies: [] as string[],
  groups: [] as number[]
})
const form = reactive(getInitialFormState())

// A store relança a falha da listagem de grupos; aqui ela vira aviso e o select fica como está.
const loadGroups = async (companyIds: number[], page: number) => {
  try {
    await groupStore.fetchByCompanies(companyIds, page)
  } catch (error) {
    notifyError(error, 'Não foi possível carregar os grupos das empresas selecionadas.')
  }
}

const groupOptions = computed(() => {
  return (groupStore.filteredGroups || []).map((group) => ({
    title: group.name,
    value: group.id,
    groupId: group.id,
    groupName: group.name,
    companyName: group.company?.name || 'Empresa não encontrada',
    raw: group
  }))
})

const rules = {
  required: (v: any) => !!v || 'Campo obrigatório',
  requiredList: (v: any[]) => (v && v.length > 0) || 'Selecione pelo menos uma empresa',
  name: (v: string) => (v && v.length >= 3) || 'Nome deve ter no mínimo 3 caracteres',
  email: (v: string) => /[a-z0-9._%+-]+@[a-z0-9.-]+\.[a-z]{2,}/i.test(v) || 'E-mail inválido',
  phone: (v: string) => (v && v.length === 15) || 'Telefone inválido',
  password: (v: string) =>
    /^(?=.*\d)(?=.*[a-z])(?=.*[A-Z])(?=.*[$*&@#])[0-9a-zA-Z$*&@#]{8,}$/.test(v) ||
    'Senha fraca (mín. 8 caracteres, 1 maiúscula, 1 minúscula, 1 número, 1 especial)',
  confirmPassword: (v: string) => v === form.password || 'As senhas não conferem'
}

watch(
  () => form.sendWelcomeEmail,
  (isSendEmail) => {
    if (isSendEmail) {
      form.password = ''
      form.confirmPassword = ''
      formRef.value?.resetValidation()
    }
  }
)

watch(
  () => form.companies,
  async (newCompanies) => {
    form.groups = []
    groupStore.currentPage = 1
    if (newCompanies && newCompanies.length > 0) {
      const companyIds = newCompanies.map((id) => Number(id))
      await loadGroups(companyIds, 1)
    } else {
      groupStore.filteredGroups = []
    }
  }
)

const resetForm = () => {
  Object.assign(form, getInitialFormState())
  loadGroups([], 1)
  formRef.value?.reset()
}

const close = () => {
  resetForm()
  emits('close')
}

const hasMoreGroups = computed(() => {
  return filteredGroups.value.length < groupStore.totalCount
})

const onIntersect = async (isIntersecting: boolean) => {
  if (!isIntersecting || isGroupsLoading.value || !hasMoreGroups.value) return
  const nextPage = groupStore.currentPage + 1
  if (filteredGroups.value.length >= groupStore.totalCount) return
  const companyIds = form.companies.map((id) => Number(id))
  await loadGroups(companyIds, nextPage)
}

const removeGroup = (id: number) => {
  form.groups = form.groups.filter((groupId) => groupId !== id)
}

const createUser = async () => {
  if (!formRef.value) return

  const { valid } = await formRef.value.validate()

  if (!valid) {
    toast.warning('Por favor, preencha corretamente os campos obrigatórios.')
    return
  }

  try {
    loading.value = true
    await Network.company.createCompanyUser({
      companies: form.companies.map((id) => Number(id)),
      companyId: Number(form.companies[0]),
      name: form.name,
      email: form.email,
      phone: form.phone1.replace(/\D/g, ''),
      registration: form.registration || undefined,
      password: form.password,
      confirmPassword: form.confirmPassword,
      fullAccess: form.fullAccess,
      sendWelcomeEmail: form.sendWelcomeEmail,
      groups: form.groups || []
    })
    toast.success('Usuário cadastrado com sucesso!')
    close()
  } catch (e) {
    // Antes: `e.response?.data?.message` extraído na mão — com o `ServiceError`
    // do service migrado o cast deixaria de casar e a mensagem do backend sumiria.
    notifyError(e, 'Não foi possível cadastrar o usuário. Verifique os dados e tente novamente.')
  } finally {
    loading.value = false
  }
}
</script>
